import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SyncIcon } from '@treams/treams-icons';
import { useAgentChatsQuery } from '@treams/treams-network';
import { FlexGap, Heading3, IconButton, StarsIcon, tokens } from '@treams/treams-ui';

import { AgentChatDetail, AgentChatRequest } from './AgentChatDetail';
import type { AgentChatSubject } from './AgentChatThread';

const Panel = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	height: 100%;
	min-height: 0;
	overflow: hidden;
`;

const Header = styled(FlexGap).attrs({
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: tokens.gap.element,
})`
	flex-shrink: 0;
	padding: ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const Title = styled(FlexGap).attrs({
	alignItems: 'center',
	gap: tokens.gap.element,
})`
	min-width: 0;
`;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	overflow: hidden;
`;

export function TreamsAIPanel(props: { subject?: AgentChatSubject; request?: AgentChatRequest }) {
	const { t } = useTranslation();
	const chatsQuery = useAgentChatsQuery();
	const startOverRef = useRef<(() => void) | null>(null);

	return (
		<Panel>
			<Header>
				<Title>
					<StarsIcon size={20} color={tokens.brand.icon} />
					<Heading3 mb={0}>{t('sidebar.aiAgent')}</Heading3>
				</Title>
				<IconButton
					type="secondary"
					icon={<SyncIcon />}
					aria-label={t('aiAgent.startOver')}
					title={t('aiAgent.startOver')}
					onClick={() => startOverRef.current?.()}
				/>
			</Header>
			<Body>
				<AgentChatDetail
					embedded
					compact
					subject={props.subject}
					chats={chatsQuery.data?.agentChats}
					onChatPersisted={() => chatsQuery.refetch()}
					startOverRef={startOverRef}
					request={props.request}
				/>
			</Body>
		</Panel>
	);
}
